'use client';

/**
 * @packageDocumentation
 * @category Component
 */

import 'BomdisoftComponents/inputs/combobox/styles.css';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { CloseRoundedIcon, ExpandMoreRoundedIcon } from 'BomdisoftComponents/icons/index';
import { placeAnchoredPopover } from 'BomdisoftComponents/menu/placement';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import { clearT, getLanguageFromLocale } from '@bomdisoft/translations';
import { useParams } from 'next/navigation';
import { type ChangeEvent, type KeyboardEvent, type ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';

export type ComboboxProps<Option> = {
  options: Option[];
  getOptionKey: (option: Option) => string;
  getOptionLabel: (option: Option) => string;
  onSelect: (option: Option | null) => void;
  inputValue: string;
  onInputChange: (value: string) => void;
  isOptionSelected?: (option: Option) => boolean;
  renderOption?: (option: Option) => ReactNode;
  label?: string;
  helperText?: string;
  placeholder?: string;
  noOptionsText?: string;
  required?: boolean;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  /** Lands on the INPUT, for a caller replacing a `TextField` whose testid was
   *  on the field itself. `testId` stays on the wrapper, so an existing consumer
   *  keeps the node it already selects; without the split, every selector that
   *  used to read `.value` starts reading a div. */
  inputTestId?: string;
  testId?: string;
  /** `bare` drops the field chrome for a combobox that is a SEGMENT of a larger
   *  control rather than a field of its own: a word set into a sentence, a
   *  cell inside a chip. The same opt-out `Select` already ships, and for the
   *  same reason. Without it these nest a bordered box inside a bordered box. */
  variant?: 'bare' | 'field';
};

export const Combobox = <Option,>({
  options,
  getOptionKey,
  getOptionLabel,
  onSelect,
  inputValue,
  onInputChange,
  isOptionSelected,
  renderOption,
  label,
  helperText,
  placeholder,
  noOptionsText,
  required,
  disabled,
  clearable,
  className,
  inputTestId,
  testId,
  variant = 'field',
}: ComboboxProps<Option>) => {
  const params = useParams();
  const language = getLanguageFromLocale(params?.lang as string) ?? 'english';
  const id = useId();
  const listboxId = `${id}-listbox`;
  const inputRef = useRef<HTMLInputElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const activeOption = activeIndex >= 0 ? options[activeIndex] : undefined;

  const optionsSignature = options.map(getOptionKey).join('\u0000');
  const [previousSignature, setPreviousSignature] = useState(optionsSignature);
  if (optionsSignature !== previousSignature) {
    setPreviousSignature(optionsSignature);
    setActiveIndex(-1);
  }

  useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const place = useCallback(() => {
    const list = listRef.current;
    const field = fieldRef.current;
    if (!list || !field) return;
    const anchor = field.getBoundingClientRect();
    list.style.setProperty('--cb-width', `${anchor.width}px`);
    list.style.maxHeight = '';
    const position = placeAnchoredPopover(anchor, { height: list.getBoundingClientRect().height, width: anchor.width }, { height: window.innerHeight, width: window.innerWidth });
    list.style.setProperty('--cb-left', `${position.left}px`);
    list.style.setProperty('--cb-top', `${position.top}px`);
    if (position.maxHeight !== undefined) list.style.maxHeight = `${position.maxHeight}px`;
  }, []);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    if (!open || disabled) {
      if (list.matches(':popover-open')) list.hidePopover();
      return;
    }
    if (!list.matches(':popover-open')) {
      list.showPopover();
      place();
      if (typeof requestAnimationFrame !== 'undefined') requestAnimationFrame(place);
    }
  }, [open, disabled, place]);

  useEffect(() => {
    if (!open || disabled) return undefined;
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    window.addEventListener('transitionend', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('transitionend', place, true);
    };
  }, [open, disabled, place]);

  useEffect(() => {
    if (activeIndex < 0) return;
    listRef.current?.querySelector('[data-active]')?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const handleSelect = (option: Option) => {
    onSelect(option);
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleClear = () => {
    onSelect(null);
    onInputChange('');
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onInputChange(event.target.value);
    setActiveIndex(-1);
    setOpen(true);
  };

  const handleBlur = () => {
    setOpen(false);
    setActiveIndex(-1);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        setOpen(true);
        setActiveIndex(options.length ? 0 : -1);
        return;
      }
      if (!options.length) return;
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex(index => (index < 0 ? (delta > 0 ? 0 : options.length - 1) : (index + delta + options.length) % options.length));
    } else if (event.key === 'Enter') {
      if (open && activeOption !== undefined) {
        event.preventDefault();
        handleSelect(activeOption);
      }
    } else if (event.key === 'Escape' && open) {
      event.preventDefault();
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  // Bound here rather than through a JSX handler: the field is a wrapper around
  // the real <input>, not an interactive element itself. Nothing is lost for
  // keyboard users — this only forwards a click on the padding to the input they
  // can already tab straight to.
  useEffect(() => {
    const field = fieldRef.current;
    if (!field) return;

    const handleFieldClick = () => {
      if (disabled) return;
      inputRef.current?.focus();
      setOpen(true);
    };

    field.addEventListener('click', handleFieldClick);
    return () => field.removeEventListener('click', handleFieldClick);
  }, [disabled]);

  return (
    <div className={joinClasses('bomdisoft-combobox', className)} data-testid={testId} data-variant={variant}>
      <div className='bomdisoft-combobox-field' data-disabled={disabled ? '' : undefined} ref={fieldRef}>
        {label && (
          <label className='bomdisoft-combobox-label' data-required={required ? '' : undefined} htmlFor={`${id}-input`} id={`${id}-label`}>
            {label}
          </label>
        )}
        <input
          aria-activedescendant={open && activeOption !== undefined ? `${listboxId}-option-${activeIndex}` : undefined}
          aria-autocomplete='list'
          aria-controls={listboxId}
          aria-describedby={helperText ? `${id}-helper` : undefined}
          aria-expanded={open}
          aria-label={label ? undefined : placeholder}
          autoComplete='off'
          className='bomdisoft-combobox-input'
          data-testid={inputTestId}
          disabled={disabled}
          id={`${id}-input`}
          onBlur={handleBlur}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          ref={inputRef}
          required={required}
          role='combobox'
          value={inputValue}
        />
        {clearable && !disabled && inputValue !== '' && (
          <Tooltip title={clearT[language]}>
            <button
              aria-label={clearT[language]}
              className='bomdisoft-combobox-clear'
              onClick={event => {
                event.stopPropagation();
                handleClear();
              }}
              onMouseDown={event => event.preventDefault()}
              type='button'
            >
              <CloseRoundedIcon size={14} />
            </button>
          </Tooltip>
        )}
        <span aria-hidden='true' className='bomdisoft-combobox-chevron' data-open={open ? '' : undefined}>
          <ExpandMoreRoundedIcon size={18} />
        </span>
      </div>
      {helperText && (
        <p className='bomdisoft-combobox-helper' id={`${id}-helper`}>
          {helperText}
        </p>
      )}
      <div aria-labelledby={label ? `${id}-label` : undefined} className='bomdisoft-combobox-listbox' id={listboxId} popover='manual' ref={listRef} role='listbox'>
        {options.length === 0 && noOptionsText && (
          <div className='bomdisoft-combobox-no-options' role='presentation'>
            {noOptionsText}
          </div>
        )}
        {options.map((option, index) => (
          <div
            aria-selected={isOptionSelected ? isOptionSelected(option) : false}
            className='bomdisoft-combobox-option'
            data-active={open && index === activeIndex ? '' : undefined}
            id={`${listboxId}-option-${index}`}
            key={getOptionKey(option)}
            onMouseDown={event => {
              if (event.button !== 0) return;
              event.preventDefault();
              handleSelect(option);
            }}
            role='option'
            tabIndex={-1}
          >
            {renderOption ? renderOption(option) : getOptionLabel(option)}
          </div>
        ))}
      </div>
    </div>
  );
};
